export type WorkspaceThemeId = 'warm_stone' | 'mist_glass' | 'forest_sage' | 'mono_notion' | 'sakura_season' | 'hawaii_slides'

export interface WorkspaceThemeDefinition {
  id: WorkspaceThemeId
  name: string
  keywords: string[]
  bodyFont: string
  captionFont: string
  colorPalette: string
  roundness: string
  elevation: string
  swatches: string[]
  previewImage: string
  cssVars: Record<`--${string}`, string>
}

export const DEFAULT_WORKSPACE_THEME_ID: WorkspaceThemeId = 'mono_notion'

export const WORKSPACE_THEMES: WorkspaceThemeDefinition[] = [
  {
    id: 'mono_notion',
    name: '极简',
    keywords: ['High Minimal', 'Editorial', 'Monochrome'],
    bodyFont: 'Inter',
    captionFont: 'Inter',
    colorPalette: 'Mono',
    roundness: 'Subtle Roundness',
    elevation: 'Flat',
    swatches: ['#FAFAFA', '#FFFFFF', '#E5E5E5', '#737373', '#1A1A1A', '#0A0A0A'],
    previewImage: 'https://res.flowdraft.cn/static/frontend/workspace-themes/D Mono Notion.png',
    cssVars: {
      '--ws-font-body': '"Inter Variable", "Inter", "Noto Sans SC", system-ui, sans-serif',
      '--ws-font-reading': '"Inter Variable", "Inter", "Noto Sans SC", system-ui, sans-serif',
      '--ws-font-caption': '"Inter Variable", "Inter", "Noto Sans SC", system-ui, sans-serif',
      '--ws-bg-app': '#FAFAFA',
      '--ws-bg-surface': '#FFFFFF',
      '--ws-bg-sidebar': '#F7F7F7',
      '--ws-bg-panel': '#FAFAFA',
      '--ws-bg-muted': '#F0F0F0',
      '--ws-bg-hover': '#EBEBEB',
      '--ws-border-soft': '#EFEFEF',
      '--ws-border': '#E5E5E5',
      '--ws-border-strong': '#D4D4D4',
      '--ws-text-primary': '#1A1A1A',
      '--ws-text-secondary': '#737373',
      '--ws-text-muted': '#A3A3A3',
      '--ws-accent': '#1A1A1A',
      '--ws-accent-soft': '#F0F0F0',
      '--ws-cta-bg': '#1A1A1A',
      '--ws-cta-text': '#FFFFFF',
      '--ws-divider': '#E5E5E5',
      '--ws-divider-glow': 'rgba(0, 0, 0, 0.04)',
      '--ws-draft-active-bg': '#EBEBEB',
      '--ws-draft-active-border': '#D4D4D4',
      '--ws-scrollbar-thumb': '#D4D4D4',
      '--ws-scrollbar-thumb-hover': '#A3A3A3',
      '--ws-radius-md': '6px',
      '--ws-radius-lg': '8px',
      '--ws-radius-xl': '10px',
      '--ws-radius-2xl': '12px',
      '--ws-shadow-soft': '0 1px 4px rgba(0, 0, 0, 0.06)',
      '--ws-shadow-strong': '0 8px 24px rgba(0, 0, 0, 0.12)',
      '--ws-shadow-shell': '-16px 0 32px rgba(0, 0, 0, 0.04)',
      '--ws-glass-bg': 'rgba(250, 250, 250, 0.92)',
      '--ws-glass-blur': '0px',
      '--ws-surface-blur': '0px',
      '--ws-preview-bg': '#F5F5F5',
      '--ws-preview-header-bg': '#FFFFFF',
      '--ws-preview-canvas-bg': '#F5F5F5',
      '--ws-user-card-bg': 'rgba(240, 240, 240, 0.92)',
      '--ws-user-card-hover-bg': 'rgba(232, 232, 232, 0.98)',
      '--ws-panel-tab-bg': '#F0F0F0',
      '--ws-panel-tab-active-bg': '#FFFFFF',
      '--ws-panel-tab-text': '#737373',
      '--ws-panel-tab-active-text': '#1A1A1A',
      '--ws-editor-bg': '#FFFFFF',
      '--ws-editor-text': '#333333',
      '--ws-editor-heading': '#1A1A1A',
      '--ws-editor-muted': '#A3A3A3',
      '--ws-editor-code-bg': '#F5F5F5',
      '--ws-editor-code-text': '#1A1A1A',
      '--ws-quote-bg': '#F5F5F5',
      '--ws-quote-border': '#A3A3A3',
      '--ws-quote-text': '#525252',
      '--ws-quote-mark': '#737373',
      '--ws-agent-composer-bg': '#2A2A2A',
      '--ws-agent-composer-border': '#3D3D3D',
      '--ws-agent-composer-text': '#FFFFFF',
      '--ws-agent-composer-placeholder': 'rgba(255, 255, 255, 0.42)',
      '--ws-agent-composer-btn-bg': '#F0F0F0',
      '--ws-agent-composer-btn-text': '#1A1A1A',
      '--ws-artifact-tab-active-bg': '#1A1A1A',
      '--ws-artifact-tab-active-text': '#FFFFFF',
      '--ws-artifact-tab-hover-bg': '#EBEBEB',
      '--ws-msg-user-bg': '#2A2A2A',
      '--ws-msg-user-text': '#FFFFFF',
      '--ws-msg-bot-bg': '#F5F5F5',
      '--ws-msg-bot-border': '#E5E5E5',
      '--ws-msg-bot-text': '#1A1A1A',
    },
  },
  {
    id: 'warm_stone',
    name: '暖石',
    keywords: ['Editorial', 'Warm Neutral', 'Low Migration Risk'],
    bodyFont: 'Inter',
    captionFont: 'JetBrains Mono',
    colorPalette: 'Warm Stone',
    roundness: 'Basic Roundness',
    elevation: 'Soft Lift',
    swatches: ['#F7F7F5', '#FBFBFA', '#E3E2E0', '#787774', '#8B6235', '#2F2B25'],
    previewImage: 'https://res.flowdraft.cn/static/frontend/workspace-themes/warm-stone-plus.png',
    cssVars: {
      '--ws-font-body': '"Inter Variable", "Inter", "Noto Sans SC", system-ui, sans-serif',
      '--ws-font-reading': '"Source Serif 4 Variable", "Source Serif 4", "Noto Serif SC", Georgia, serif',
      '--ws-font-caption': '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace',
      '--ws-bg-app': '#F7F7F5',
      '--ws-bg-surface': '#FFFFFF',
      '--ws-bg-sidebar': '#F7F7F5',
      '--ws-bg-panel': '#FBFAF8',
      '--ws-bg-muted': '#F1F0EE',
      '--ws-bg-hover': '#ECE9E2',
      '--ws-border-soft': '#ECEAE6',
      '--ws-border': '#E3E2E0',
      '--ws-border-strong': '#D2D0CC',
      '--ws-text-primary': '#37352F',
      '--ws-text-secondary': '#787774',
      '--ws-text-muted': '#9D9D9B',
      '--ws-accent': '#8B6235',
      '--ws-accent-soft': '#F5EDE2',
      '--ws-cta-bg': '#2F2B25',
      '--ws-cta-text': '#FFFFFF',
      '--ws-divider': '#DFDBD3',
      '--ws-divider-glow': 'rgba(68, 56, 42, 0.06)',
      '--ws-draft-active-bg': '#E8E3D9',
      '--ws-draft-active-border': '#D9D2C5',
      '--ws-scrollbar-thumb': '#CBC7BE',
      '--ws-scrollbar-thumb-hover': '#A59F93',
      '--ws-radius-md': '8px',
      '--ws-radius-lg': '10px',
      '--ws-radius-xl': '12px',
      '--ws-radius-2xl': '16px',
      '--ws-shadow-soft': '0 2px 8px rgba(23, 20, 16, 0.08)',
      '--ws-shadow-strong': '0 10px 28px rgba(23, 20, 16, 0.16)',
      '--ws-shadow-shell': '-20px 0 40px rgba(20, 16, 12, 0.06)',
      '--ws-glass-bg': 'rgba(251, 251, 250, 0.9)',
      '--ws-glass-blur': '0px',
      '--ws-surface-blur': '0px',
      '--ws-preview-bg': '#F2F1EE',
      '--ws-preview-header-bg': '#FFFFFF',
      '--ws-preview-canvas-bg': '#F2F1EE',
      '--ws-user-card-bg': 'rgba(240, 238, 234, 0.92)',
      '--ws-user-card-hover-bg': 'rgba(232, 228, 221, 0.98)',
      '--ws-panel-tab-bg': '#EFEDED',
      '--ws-panel-tab-active-bg': '#FFFFFF',
      '--ws-panel-tab-text': '#787774',
      '--ws-panel-tab-active-text': '#37352F',
      '--ws-editor-bg': '#FFFFFF',
      '--ws-editor-text': '#44403C',
      '--ws-editor-heading': '#37352F',
      '--ws-editor-muted': '#9D9D9B',
      '--ws-editor-code-bg': '#F7F7F5',
      '--ws-editor-code-text': '#37352F',
      '--ws-quote-bg': '#F6F4F0',
      '--ws-quote-border': '#B1AA9C',
      '--ws-quote-text': '#57534E',
      '--ws-quote-mark': '#8A8376',
      '--ws-agent-composer-bg': '#4A473D',
      '--ws-agent-composer-border': '#686458',
      '--ws-agent-composer-text': '#FFFFFF',
      '--ws-agent-composer-placeholder': 'rgba(255, 255, 255, 0.5)',
      '--ws-agent-composer-btn-bg': '#E8E3D9',
      '--ws-agent-composer-btn-text': '#2F2B25',
      '--ws-artifact-tab-active-bg': '#2E2C28',
      '--ws-artifact-tab-active-text': '#FFFFFF',
      '--ws-artifact-tab-hover-bg': '#E8E4DD',
      '--ws-msg-user-bg': '#4A473D',
      '--ws-msg-user-text': '#FFFFFF',
      '--ws-msg-bot-bg': '#F5F3ED',
      '--ws-msg-bot-border': '#EDE7DC',
      '--ws-msg-bot-text': '#3A362F',
    },
  },
  {
    id: 'mist_glass',
    name: '雾蓝',
    keywords: ['Calm Focus', 'Glass Texture', 'Blue-Gray'],
    bodyFont: 'Inter',
    captionFont: 'JetBrains Mono',
    colorPalette: 'Mist Blue',
    roundness: 'Basic Roundness',
    elevation: 'Soft Lift',
    swatches: ['#F3F6FB', '#F8FBFF', '#D8E1EC', '#667A95', '#3B82F6', '#2D4668'],
    previewImage: 'https://res.flowdraft.cn/static/frontend/workspace-themes/mist-blue.png',
    cssVars: {
      '--ws-font-body': '"Inter Variable", "Inter", "Noto Sans SC", system-ui, sans-serif',
      '--ws-font-reading': '"Source Serif 4 Variable", "Source Serif 4", "Noto Serif SC", Georgia, serif',
      '--ws-font-caption': '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace',
      '--ws-bg-app': '#F3F6FB',
      '--ws-bg-surface': 'rgba(251, 253, 255, 0.86)',
      '--ws-bg-sidebar': 'rgba(242, 247, 253, 0.9)',
      '--ws-bg-panel': 'rgba(248, 251, 255, 0.9)',
      '--ws-bg-muted': '#EAF0F8',
      '--ws-bg-hover': '#DEE8F5',
      '--ws-border-soft': '#E5ECF5',
      '--ws-border': '#D8E1EC',
      '--ws-border-strong': '#C4D2E3',
      '--ws-text-primary': '#2A3D59',
      '--ws-text-secondary': '#667A95',
      '--ws-text-muted': '#8A9AAF',
      '--ws-accent': '#3B82F6',
      '--ws-accent-soft': '#E2ECFB',
      '--ws-cta-bg': '#2D4668',
      '--ws-cta-text': '#FFFFFF',
      '--ws-divider': 'rgba(155, 177, 207, 0.62)',
      '--ws-divider-glow': 'rgba(80, 117, 170, 0.18)',
      '--ws-draft-active-bg': 'rgba(223, 234, 250, 0.9)',
      '--ws-draft-active-border': 'rgba(148, 173, 208, 0.75)',
      '--ws-scrollbar-thumb': '#A9BED9',
      '--ws-scrollbar-thumb-hover': '#839FBE',
      '--ws-radius-md': '8px',
      '--ws-radius-lg': '10px',
      '--ws-radius-xl': '12px',
      '--ws-radius-2xl': '16px',
      '--ws-shadow-soft': '0 4px 14px rgba(33, 54, 87, 0.12)',
      '--ws-shadow-strong': '0 14px 34px rgba(33, 54, 87, 0.2)',
      '--ws-shadow-shell': '-20px 0 44px rgba(26, 43, 70, 0.16)',
      '--ws-glass-bg': 'rgba(246, 250, 255, 0.7)',
      '--ws-glass-blur': '14px',
      '--ws-surface-blur': '12px',
      '--ws-preview-bg': '#EAF0F8',
      '--ws-preview-header-bg': '#FDFEFF',
      '--ws-preview-canvas-bg': '#EAF0F8',
      '--ws-user-card-bg': 'rgba(232, 239, 248, 0.92)',
      '--ws-user-card-hover-bg': 'rgba(220, 231, 245, 0.98)',
      '--ws-panel-tab-bg': '#EAF0F8',
      '--ws-panel-tab-active-bg': '#FFFFFF',
      '--ws-panel-tab-text': '#667A95',
      '--ws-panel-tab-active-text': '#2A3D59',
      '--ws-editor-bg': '#FDFEFF',
      '--ws-editor-text': '#2F425C',
      '--ws-editor-heading': '#2A3D59',
      '--ws-editor-muted': '#8A9AAF',
      '--ws-editor-code-bg': '#EFF4FB',
      '--ws-editor-code-text': '#2A3D59',
      '--ws-quote-bg': '#EEF4FB',
      '--ws-quote-border': '#8AA9CC',
      '--ws-quote-text': '#4A6584',
      '--ws-quote-mark': '#6E8FB6',
      '--ws-agent-composer-bg': '#5577A3',
      '--ws-agent-composer-border': '#6C89AF',
      '--ws-agent-composer-text': '#FFFFFF',
      '--ws-agent-composer-placeholder': 'rgba(255, 255, 255, 0.5)',
      '--ws-agent-composer-btn-bg': '#EAF0F8',
      '--ws-agent-composer-btn-text': '#2A3D59',
      '--ws-artifact-tab-active-bg': '#4E6F98',
      '--ws-artifact-tab-active-text': '#FFFFFF',
      '--ws-artifact-tab-hover-bg': '#DCE8F5',
      '--ws-msg-user-bg': '#5577A3',
      '--ws-msg-user-text': '#FFFFFF',
      '--ws-msg-bot-bg': '#F2F5FB',
      '--ws-msg-bot-border': '#E5ECF5',
      '--ws-msg-bot-text': '#2A3D59',
    },
  },
  {
    id: 'forest_sage',
    name: '森林',
    keywords: ['Inline Friendly', 'Elegant', 'Natural'],
    bodyFont: 'Inter',
    captionFont: 'JetBrains Mono',
    colorPalette: 'Forest Sage',
    roundness: 'Basic Roundness',
    elevation: 'Soft Lift',
    swatches: ['#F4F7F2', '#FCFDFB', '#D8E1D3', '#5C7065', '#3F915D', '#2D4A3C'],
    previewImage: 'https://res.flowdraft.cn/static/frontend/workspace-themes/forest-paper.png',
    cssVars: {
      '--ws-font-body': '"Inter Variable", "Inter", "Noto Sans SC", system-ui, sans-serif',
      '--ws-font-reading': '"Source Serif 4 Variable", "Source Serif 4", "Noto Serif SC", Georgia, serif',
      '--ws-font-caption': '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace',
      '--ws-bg-app': '#F4F7F2',
      '--ws-bg-surface': '#FCFDFB',
      '--ws-bg-sidebar': '#F1F5EE',
      '--ws-bg-panel': '#FBFCF7',
      '--ws-bg-muted': '#E7EDE2',
      '--ws-bg-hover': '#DDE7D8',
      '--ws-border-soft': '#E4EADD',
      '--ws-border': '#D8E1D3',
      '--ws-border-strong': '#C5D1BF',
      '--ws-text-primary': '#274036',
      '--ws-text-secondary': '#5C7065',
      '--ws-text-muted': '#7E9083',
      '--ws-accent': '#3F915D',
      '--ws-accent-soft': '#DFEBDD',
      '--ws-cta-bg': '#2D4A3C',
      '--ws-cta-text': '#FFFFFF',
      '--ws-divider': '#CFD9C8',
      '--ws-divider-glow': 'rgba(72, 103, 83, 0.09)',
      '--ws-draft-active-bg': '#E2EBDD',
      '--ws-draft-active-border': '#C8D5C2',
      '--ws-scrollbar-thumb': '#B4C5B3',
      '--ws-scrollbar-thumb-hover': '#8FA58E',
      '--ws-radius-md': '8px',
      '--ws-radius-lg': '10px',
      '--ws-radius-xl': '12px',
      '--ws-radius-2xl': '16px',
      '--ws-shadow-soft': '0 3px 12px rgba(33, 49, 40, 0.1)',
      '--ws-shadow-strong': '0 12px 30px rgba(33, 49, 40, 0.16)',
      '--ws-shadow-shell': '-20px 0 40px rgba(30, 45, 36, 0.1)',
      '--ws-glass-bg': 'rgba(247, 250, 245, 0.9)',
      '--ws-glass-blur': '0px',
      '--ws-surface-blur': '0px',
      '--ws-preview-bg': '#E9EFE3',
      '--ws-preview-header-bg': '#FCFDF9',
      '--ws-preview-canvas-bg': '#EEF3E8',
      '--ws-user-card-bg': 'rgba(234, 239, 227, 0.92)',
      '--ws-user-card-hover-bg': 'rgba(224, 233, 214, 0.98)',
      '--ws-panel-tab-bg': '#E7EDE0',
      '--ws-panel-tab-active-bg': '#FCFDF9',
      '--ws-panel-tab-text': '#5C7065',
      '--ws-panel-tab-active-text': '#274036',
      '--ws-editor-bg': '#FFFFFF',
      '--ws-editor-text': '#36513F',
      '--ws-editor-heading': '#274036',
      '--ws-editor-muted': '#7E9083',
      '--ws-editor-code-bg': '#F1F6EC',
      '--ws-editor-code-text': '#2E4A37',
      '--ws-quote-bg': '#EDF4E8',
      '--ws-quote-border': '#8BAA8B',
      '--ws-quote-text': '#4F6657',
      '--ws-quote-mark': '#6E8A74',
      '--ws-agent-composer-bg': '#536A4D',
      '--ws-agent-composer-border': '#6B8164',
      '--ws-agent-composer-text': '#FFFFFF',
      '--ws-agent-composer-placeholder': 'rgba(255, 255, 255, 0.5)',
      '--ws-agent-composer-btn-bg': '#EAEFE3',
      '--ws-agent-composer-btn-text': '#274036',
      '--ws-artifact-tab-active-bg': '#4D6A48',
      '--ws-artifact-tab-active-text': '#FFFFFF',
      '--ws-artifact-tab-hover-bg': '#EAEFE3',
      '--ws-msg-user-bg': '#536A4D',
      '--ws-msg-user-text': '#FFFFFF',
      '--ws-msg-bot-bg': '#F4F6EE',
      '--ws-msg-bot-border': '#E8EEDF',
      '--ws-msg-bot-text': '#274036',
    },
  },
  {
    id: 'sakura_season',
    name: '落樱',
    keywords: ['Petal Soft', 'Editorial', 'Spring Journal'],
    bodyFont: 'Inter',
    captionFont: 'JetBrains Mono',
    colorPalette: 'Sakura Blossom',
    roundness: 'Basic Roundness',
    elevation: 'Soft Lift',
    swatches: ['#F9F7F7', '#FFFFFF', '#E9CAD6', '#9A8A8F', '#6E3E55', '#3E3438'],
    previewImage: 'https://res.flowdraft.cn/static/frontend/workspace-themes/sakura-season.png',
    cssVars: {
      '--ws-font-body': '"Inter Variable", "Inter", "Noto Sans SC", system-ui, sans-serif',
      '--ws-font-reading': '"Source Serif 4 Variable", "Source Serif 4", "Noto Serif SC", Georgia, serif',
      '--ws-font-caption': '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace',
      '--ws-bg-app': '#F9F7F7',
      '--ws-bg-surface': '#FFFFFF',
      '--ws-bg-sidebar': '#F9F7F7',
      '--ws-bg-panel': '#F5EDF0',
      '--ws-bg-muted': '#F5EDF0',
      '--ws-bg-hover': '#F2DDE4',
      '--ws-border-soft': '#EEDAE0',
      '--ws-border': '#E9CAD6',
      '--ws-border-strong': '#DCBAC6',
      '--ws-text-primary': '#3E3438',
      '--ws-text-secondary': '#6E3E55',
      '--ws-text-muted': '#9A8A8F',
      '--ws-accent': '#6E3E55',
      '--ws-accent-soft': '#F2DDE4',
      '--ws-cta-bg': '#6E3E55',
      '--ws-cta-text': '#FFFFFF',
      '--ws-divider': '#E9CAD6',
      '--ws-divider-glow': 'rgba(110, 62, 85, 0.05)',
      '--ws-draft-active-bg': '#F2DDE4',
      '--ws-draft-active-border': '#E9CAD6',
      '--ws-scrollbar-thumb': '#DCBAC6',
      '--ws-scrollbar-thumb-hover': '#C293A3',
      '--ws-radius-md': '8px',
      '--ws-radius-lg': '10px',
      '--ws-radius-xl': '12px',
      '--ws-radius-2xl': '16px',
      '--ws-shadow-soft': '0 2px 8px rgba(62, 52, 56, 0.07)',
      '--ws-shadow-strong': '0 10px 28px rgba(62, 52, 56, 0.14)',
      '--ws-shadow-shell': '-20px 0 40px rgba(62, 52, 56, 0.05)',
      '--ws-glass-bg': 'rgba(249, 247, 247, 0.9)',
      '--ws-glass-blur': '0px',
      '--ws-surface-blur': '0px',
      '--ws-preview-bg': '#F5EDF0',
      '--ws-preview-header-bg': '#FFFFFF',
      '--ws-preview-canvas-bg': '#F5EDF0',
      '--ws-user-card-bg': 'rgba(245, 237, 240, 0.92)',
      '--ws-user-card-hover-bg': 'rgba(242, 221, 228, 0.95)',
      '--ws-panel-tab-bg': '#F5EDF0',
      '--ws-panel-tab-active-bg': '#FFFFFF',
      '--ws-panel-tab-text': '#9A8A8F',
      '--ws-panel-tab-active-text': '#3E3438',
      '--ws-editor-bg': '#FFFFFF',
      '--ws-editor-text': '#3E3438',
      '--ws-editor-heading': '#6E3E55',
      '--ws-editor-muted': '#9A8A8F',
      '--ws-editor-code-bg': '#F9F7F7',
      '--ws-editor-code-text': '#6E3E55',
      '--ws-quote-bg': '#F5EDF0',
      '--ws-quote-border': '#DCBAC6',
      '--ws-quote-text': '#8D6778',
      '--ws-quote-mark': '#9A8A8F',
      '--ws-agent-composer-bg': '#E9CAD6',
      '--ws-agent-composer-border': '#DCBAC6',
      '--ws-agent-composer-text': '#6E3E55',
      '--ws-agent-composer-placeholder': 'rgba(110, 62, 85, 0.35)',
      '--ws-agent-composer-btn-bg': '#6E3E55',
      '--ws-agent-composer-btn-text': '#F9F7F7',
      '--ws-artifact-tab-active-bg': '#6E3E55',
      '--ws-artifact-tab-active-text': '#FFFFFF',
      '--ws-artifact-tab-hover-bg': '#F2DDE4',
      '--ws-msg-user-bg': '#6E3E55',
      '--ws-msg-user-text': '#FFFFFF',
      '--ws-msg-bot-bg': '#EFE6EA',
      '--ws-msg-bot-border': '#EDD9E0',
      '--ws-msg-bot-text': '#3E3438',
    },
  },
  {
    id: 'hawaii_slides',
    name: '夏威夷',
    keywords: ['Tropical', 'Warm Coral', 'Lagoon Teal'],
    bodyFont: 'Inter',
    captionFont: 'JetBrains Mono',
    colorPalette: 'Hawaii Tropical',
    roundness: 'Basic Roundness',
    elevation: 'Soft Lift',
    swatches: ['#FAE3D9', '#FFFDFB', '#FFB6B9', '#61C0BF', '#7A4A57', '#5E5050'],
    previewImage: 'https://res.flowdraft.cn/static/frontend/workspace-themes/hawaii-slides.png',
    cssVars: {
      '--ws-font-body': '"Inter Variable", "Inter", "Noto Sans SC", system-ui, sans-serif',
      '--ws-font-reading': '"Source Serif 4 Variable", "Source Serif 4", "Noto Serif SC", Georgia, serif',
      '--ws-font-caption': '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace',
      '--ws-bg-app': '#FAE3D9',
      '--ws-bg-surface': '#FFFDFB',
      '--ws-bg-sidebar': '#FAE3D9',
      '--ws-bg-panel': '#F7ECE7',
      '--ws-bg-muted': '#F6EDEA',
      '--ws-bg-hover': '#FFE2E1',
      '--ws-border-soft': '#FFD1D3',
      '--ws-border': '#F3C4C7',
      '--ws-border-strong': '#E5ACAF',
      '--ws-text-primary': '#5E5050',
      '--ws-text-secondary': '#7A4A57',
      '--ws-text-muted': '#96717A',
      '--ws-accent': '#61C0BF',
      '--ws-accent-soft': '#DDF1EE',
      '--ws-cta-bg': '#61C0BF',
      '--ws-cta-text': '#F8FFFE',
      '--ws-divider': '#F3C4C7',
      '--ws-divider-glow': 'rgba(122, 74, 87, 0.06)',
      '--ws-draft-active-bg': '#FFE2E1',
      '--ws-draft-active-border': '#FFB6B9',
      '--ws-scrollbar-thumb': '#E5ACAF',
      '--ws-scrollbar-thumb-hover': '#D5979D',
      '--ws-radius-md': '8px',
      '--ws-radius-lg': '10px',
      '--ws-radius-xl': '12px',
      '--ws-radius-2xl': '16px',
      '--ws-shadow-soft': '0 2px 8px rgba(94, 80, 80, 0.08)',
      '--ws-shadow-strong': '0 10px 28px rgba(94, 80, 80, 0.16)',
      '--ws-shadow-shell': '-20px 0 40px rgba(94, 80, 80, 0.06)',
      '--ws-glass-bg': 'rgba(250, 227, 217, 0.9)',
      '--ws-glass-blur': '0px',
      '--ws-surface-blur': '0px',
      '--ws-preview-bg': '#F6EDEA',
      '--ws-preview-header-bg': '#FFFDFB',
      '--ws-preview-canvas-bg': '#F6EDEA',
      '--ws-user-card-bg': 'rgba(247, 236, 231, 0.92)',
      '--ws-user-card-hover-bg': 'rgba(255, 226, 225, 0.98)',
      '--ws-panel-tab-bg': '#F6EDEA',
      '--ws-panel-tab-active-bg': '#FFFDFB',
      '--ws-panel-tab-text': '#96717A',
      '--ws-panel-tab-active-text': '#5E5050',
      '--ws-editor-bg': '#FFFDFB',
      '--ws-editor-text': '#5E5050',
      '--ws-editor-heading': '#7A4A57',
      '--ws-editor-muted': '#96717A',
      '--ws-editor-code-bg': '#FFF4F2',
      '--ws-editor-code-text': '#7A4A57',
      '--ws-quote-bg': '#FFF4F2',
      '--ws-quote-border': '#F3C4C7',
      '--ws-quote-text': '#8A7072',
      '--ws-quote-mark': '#96717A',
      '--ws-agent-composer-bg': '#3D7170',
      '--ws-agent-composer-border': '#4E8A8A',
      '--ws-agent-composer-text': '#FFFFFF',
      '--ws-agent-composer-placeholder': 'rgba(255, 255, 255, 0.5)',
      '--ws-agent-composer-btn-bg': '#BBDED6',
      '--ws-agent-composer-btn-text': '#355E60',
      '--ws-artifact-tab-active-bg': '#61C0BF',
      '--ws-artifact-tab-active-text': '#FFFFFF',
      '--ws-artifact-tab-hover-bg': '#DDF1EE',
      '--ws-msg-user-bg': '#3D7170',
      '--ws-msg-user-text': '#FFFFFF',
      '--ws-msg-bot-bg': '#F0E4DE',
      '--ws-msg-bot-border': '#F3DDD8',
      '--ws-msg-bot-text': '#5E5050',
    },
  },
]

const WORKSPACE_THEME_MAP = new Map<WorkspaceThemeId, WorkspaceThemeDefinition>(
  WORKSPACE_THEMES.map((theme) => [theme.id, theme])
)

const WORKSPACE_THEME_VAR_KEYS = Array.from(
  new Set(WORKSPACE_THEMES.flatMap((theme) => Object.keys(theme.cssVars)))
)

export function getWorkspaceTheme(themeId: WorkspaceThemeId): WorkspaceThemeDefinition {
  return WORKSPACE_THEME_MAP.get(themeId) ?? WORKSPACE_THEME_MAP.get(DEFAULT_WORKSPACE_THEME_ID)!
}

export function applyWorkspaceTheme(themeId: WorkspaceThemeId): void {
  if (typeof document === 'undefined') return

  const theme = getWorkspaceTheme(themeId)
  document.body.dataset.workspaceTheme = theme.id
  Object.entries(theme.cssVars).forEach(([key, value]) => {
    document.body.style.setProperty(key, value)
  })
}

export function clearWorkspaceTheme(): void {
  if (typeof document === 'undefined') return

  delete document.body.dataset.workspaceTheme
  WORKSPACE_THEME_VAR_KEYS.forEach((key) => {
    document.body.style.removeProperty(key)
  })
}
